<template>
  <div class="review-sidebar-list">
    <div
      v-if="
        showRatingFilters &&
        setRatingFilter &&
        hasReviewBreakdownData(reviewBreakdown)
      "
      class="rating-filters"
      role="group"
      :aria-label="t('review.sidebar.filterAll')"
      data-test-id="review-sidebar-rating-filters"
    >
      <button
        type="button"
        class="rating-filter-btn"
        :class="{ active: selectedRating === null }"
        data-test-id="rating-filter-all"
        @click="setRatingFilter(null)"
      >
        {{ t('review.sidebar.filterAll') }}
      </button>
      <button
        v-for="rating in [5, 4, 3, 2, 1]"
        :key="rating"
        type="button"
        class="rating-filter-btn rating-filter-btn--stars"
        :data-test-id="`rating-filter-${rating}`"
        :class="{
          active: selectedRating === rating,
          'rating-filter-btn--disabled': isRatingDisabledForRating(rating),
        }"
        :disabled="isRatingDisabledForRating(rating)"
        :aria-label="t('review.sidebar.filterRating', { n: rating })"
        @click="!isRatingDisabledForRating(rating) && setRatingFilter?.(rating)"
      >
        <Tooltip
          :tooltip-text="t('review.sidebar.noRatingReviews', { n: rating })"
          :disabled="getRatingCountForRating(rating) > 0"
          class="inline-flex items-center gap-1"
          position="down"
          type="span"
        >
          <IconStarFull class="rating-filter-btn__star" aria-hidden />
          {{ rating }}
        </Tooltip>
      </button>
    </div>
    <template v-for="(review, index) in reviews" :key="review.id">
      <hr v-if="index > 0" class="divider" />
      <div
        class="item"
        :class="{
          hover: isReviewHasDetail(review),
        }"
        @click="clickItem(review)"
      >
        <div class="flex justify-between items-start mb-3 gap-3">
          <div class="name">{{ review.customer }}</div>
          <ReviewVerifiedBadge v-if="review?.verified" />
        </div>

        <div class="review-text">
          <div class="text" v-html="review.review" />
          <div v-if="isReviewHasDetail(review)" class="read-more">
            {{ t('general.kftReadMore.readMoreLabel') }}
          </div>
        </div>

        <div v-if="review.media?.data?.length" class="flex gap-2 mt-3">
          <img
            v-for="img in review.media?.data"
            :key="img.id"
            :src="getImageUrl(img)"
            class="w-[80px] h-[80px] object-cover rounded-lg"
            alt=""
          />
        </div>

        <div v-if="review.created_at" class="date">
          {{
            formatDate(
              review.created_at,
              $i18n.locale.toString(),
              'MMMM DD, YYYY'
            )
          }}
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { formatDate } from '~/utils/dateHelpers'
import {
  getRatingCount,
  hasReviewBreakdownData,
  isRatingDisabled,
} from '~/utils/reviewSidebarHelpers'
import type { Review } from '~/composables/types/event'
import IconStarFull from '~/assets/icons/review/icon-star-full.svg?component'

interface Props {
  reviews: Review[]
  averageRating: string
  reviewCount: number
  showRatingFilters?: boolean
  selectedRating?: number | null
  setRatingFilter?: (rating: number | null) => void
  reviewBreakdown?: Record<string, number>
}

const props = withDefaults(defineProps<Props>(), {
  reviews: () => [],
  averageRating: '',
  reviewCount: 0,
  showRatingFilters: false,
  selectedRating: null,
  setRatingFilter: undefined,
  reviewBreakdown: undefined,
})

const getRatingCountForRating = (rating: number): number =>
  getRatingCount(rating, props.reviewBreakdown)

const isRatingDisabledForRating = (rating: number): boolean =>
  isRatingDisabled(rating, props.reviewBreakdown)

const $i18n = useI18n()
const { t } = useI18n()

const emit = defineEmits(['click'])

const clickItem = (review: Review): void => {
  if (isReviewHasDetail(review)) {
    emit('click', review.id)
  }
}

const isReviewHasDetail = (review: Review): boolean => {
  return !!review?.media?.data?.length
}

const getImageUrl = (image) => {
  if (image.url.includes('.mp4')) return '/_nuxt3-static/reviews/icon-play.png'

  return image.thumb || image.cdn_url || image.url
}
</script>

<style lang="postcss" scoped>
.review-sidebar-list {
  @apply flex flex-col;
}

.divider {
  @apply border-0 border-t border-gray-200 my-0 flex-shrink-0;
}

.item {
  @apply p-4 ease-in-out duration-300;

  &.hover {
    @apply cursor-pointer;

    &:hover {
      @apply opacity-90;
    }
  }
}

.name {
  @apply text-base font-medium;
}

.read-more,
.text {
  @apply text-stone-500 text-base;
}

.read-more {
  @apply underline;
}

.date {
  @apply mt-3 text-xs text-gray-500;
}

.rating-filters {
  @apply flex flex-wrap gap-2 px-4 border-b border-gray-200 pb-4;
}

.rating-filter-btn {
  @apply px-2.5 md:px-3 py-1.5 text-sm rounded-full border border-gray-200 bg-white text-gray-700
    transition-colors duration-200 inline-flex items-center gap-1;

  &:hover {
    @apply border-gray-300 bg-gray-50;
  }

  &.active {
    @apply border-teal-800 bg-teal-100/50 text-teal-800 font-medium;
  }

  &:disabled,
  &--disabled {
    @apply cursor-not-allowed grayscale;

    * {
      @apply cursor-not-allowed;
    }
  }

  &--stars {
    @apply gap-px;
  }

  &__star {
    @apply w-3.5 h-3.5 mr-0.5 flex-shrink-0;
    fill: currentColor;
  }

  &.active &__star :deep(path) {
    @apply fill-teal-800;
  }
}
</style>
